import { useMemo, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Search, PawPrint, X } from "lucide-react";
import SectionHeading from "./SectionHeading";
import ProductCard from "./ProductCard";
import Reveal from "./Reveal";
import products from "@/data/products.json";

const FILTERS = [
    { id: "todos", label: "Todos" },
    { id: "caes", label: "Cães" },
    { id: "gatos", label: "Gatos" },
    { id: "nutricao", label: "Alimentação" },
    { id: "brinquedos", label: "Brinquedos" },
    { id: "acessorios", label: "Acessórios" },
];

const normalize = (s) => s.normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase();

export default function Catalog({ activeCategory, setActiveCategory }) {
    const [query, setQuery] = useState("");

    const filtered = useMemo(() => {
        const q = normalize(query.trim());
        return products.filter((p) => {
            const inCategory = activeCategory === "todos" || p.categories.includes(activeCategory);
            const matchesQuery = !q || normalize(p.name).includes(q) || normalize(p.description).includes(q);
            return inCategory && matchesQuery;
        });
    }, [query, activeCategory]);

    return (
        <section id="catalogo" data-testid="catalog-section" className="relative bg-cream-card/60 py-20 lg:py-28">
            <div className="container-x">
                <SectionHeading
                    eyebrow="Catálogo demo"
                    title={
                        <>
                            Produtos que abraçam <em className="text-coral">de verdade</em>
                        </>
                    }
                    description="Uma seleção demonstrativa da boutique — todos os valores são claramente fictícios e nada é vendido."
                />

                <Reveal delay={0.15} className="mt-10">
                    <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
                        <div
                            className="flex gap-2 overflow-x-auto pb-1 no-scrollbar"
                            role="tablist"
                            aria-label="Filtrar por categoria"
                        >
                            {FILTERS.map((f) => (
                                <button
                                    key={f.id}
                                    type="button"
                                    role="tab"
                                    aria-selected={activeCategory === f.id}
                                    data-testid={`category-filter-${f.id === "todos" ? "all" : f.id}`}
                                    onClick={() => setActiveCategory(f.id)}
                                    className={`shrink-0 rounded-full border px-4 py-2.5 text-[13px] font-bold transition-all duration-300 ${
                                        activeCategory === f.id
                                            ? "border-forest bg-forest text-cream shadow-[0_10px_24px_rgba(23,61,53,0.25)]"
                                            : "border-sand bg-cream-card text-forest/70 hover:border-coral hover:text-forest"
                                    }`}
                                >
                                    {f.label}
                                </button>
                            ))}
                        </div>

                        <div className="relative w-full lg:w-80">
                            <Search
                                className="pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-forest/40"
                                aria-hidden="true"
                            />
                            <input
                                type="search"
                                value={query}
                                onChange={(e) => setQuery(e.target.value)}
                                placeholder="Buscar produto demo..."
                                data-testid="catalog-search-input"
                                aria-label="Buscar produtos"
                                className="w-full rounded-full border border-sand bg-cream-card py-3 pl-11 pr-10 text-sm text-forest placeholder:text-forest/35 focus:border-coral focus:outline-none focus:ring-2 focus:ring-coral/30"
                            />
                            {query && (
                                <button
                                    type="button"
                                    aria-label="Limpar busca"
                                    data-testid="catalog-search-clear"
                                    onClick={() => setQuery("")}
                                    className="absolute right-3 top-1/2 -translate-y-1/2 rounded-full p-1 text-forest/50 hover:text-coral-dark"
                                >
                                    <X className="h-4 w-4" />
                                </button>
                            )}
                        </div>
                    </div>
                </Reveal>

                <p className="mt-6 text-xs font-bold uppercase tracking-[0.22em] text-forest/45" data-testid="catalog-count">
                    {filtered.length} produto{filtered.length !== 1 ? "s" : ""} · preços fictícios
                </p>

                {filtered.length > 0 ? (
                    <motion.div layout className="mt-8 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4 lg:gap-6">
                        <AnimatePresence mode="popLayout">
                            {filtered.map((product, i) => (
                                <ProductCard key={product.id} product={product} index={i} />
                            ))}
                        </AnimatePresence>
                    </motion.div>
                ) : (
                    <div
                        data-testid="catalog-empty-state"
                        className="mt-12 flex flex-col items-center gap-4 rounded-[2rem] border border-dashed border-sand bg-cream-card px-6 py-16 text-center"
                    >
                        <span className="grid h-16 w-16 place-items-center rounded-full bg-coral/15">
                            <PawPrint className="h-7 w-7 text-coral" />
                        </span>
                        <p className="font-serif text-xl font-bold text-forest">Nada encontrado por aqui</p>
                        <p className="max-w-sm text-sm text-graphite-muted">
                            Nenhum produto demonstrativo corresponde a “{query}”. Tente outra busca ou limpe os
                            filtros.
                        </p>
                        <button
                            type="button"
                            data-testid="catalog-empty-reset"
                            onClick={() => {
                                setQuery("");
                                setActiveCategory("todos");
                            }}
                            className="rounded-full bg-forest px-6 py-3 text-sm font-bold text-cream transition-all hover:bg-forest-light"
                        >
                            Limpar filtros
                        </button>
                    </div>
                )}
            </div>
        </section>
    );
}
